import { Component, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import {
  IconoirCustomMoreVerticalAlt,
  IconoirEditPencil,
  IconoirMoreHoriz,
  IconoirPagePlusIn,
  MotifActionIcDelete24px,
  MotifAvIcPlaylistAdd24px,
  IconoirPlus,
} from '@ey-xd/motif-icon';
import {
  MotifIconModule,
  MotifTooltipModule,
  MotifDropdownModule,
  MotifModalModule,
  Placement,
  ScrollStrategy,
  MotifModal,
  MotifButtonModule,
} from '@ey-xd/ng-motif';
import { CommonModule } from '@angular/common';
import {
  ApplicationConstants,
  CommonConstants,
  DeliverablesConstants,
} from '@sa/app/shared/constants/ApplicationConstants';
import { CommonService } from '@sa/app/shared/services/common.service';
import { AgentService } from '@sa/shared/services/agent-service';
import { ConfirmDialogComponent } from '@sa/app/shared/components/confirm-dialog/confirm-dialog.component';
import { MilestonePanelComponent } from '../../milestone-panel/milestone-panel.component';
import { AddTaskPanelComponent } from '@sa/app/components/add-task-panel/add-task-panel.component';
import { LoggerService } from '@sa/app/core/services/logger.service';
import { WorkspaceService } from '@sa/app/shared/services/workspace.service';
import { WorkspaceTemplateService } from '@sa/app/shared/services/workspace-template.service';
import { RoleUtilsService } from '@sa/app/shared/utility/user-auth-utility';
import { LocalStorageService } from '@sa/app/shared/services/local-storage.service';
import { HeaderStateService } from '@sa/shared/services/header-state.service';
import { UserAccessService } from '@sa/shared/services/user-access.service';
import { WorkspaceItemDetailsResponse } from '@sa/app/shared/interfaces/WorkspaceItemDetailsResponse';
import { AppRole, RoleName } from '@sa/app/shared/enums/user-roles';
import { AddMilestoneTemplatePanelComponent } from '../../deliverable-management/deliverable-template/add-milestone-template-panel/add-milestone-template-panel.component';
import { AddTaskTemplatePanelComponent } from '../../deliverable-management/deliverable-template/add-task-template-panel/add-task-template-panel.component';
import { WorkspaceTemplateItemDetailsResponse } from '@sa/app/shared/interfaces/WorkspaceTemplateItemDetailsResponse';

/**
 * ContextMenuComponent renders a context menu for ag-Grid table rows, providing actions such as edit, add milestone, add task, and delete.
 * It supports custom icon mapping and accessibility features, and integrates with Motif and ag-Grid libraries.
 */
@Component({
  selector: 'app-template-context-menu',
  standalone: true,
  imports: [
    MotifIconModule,
    MotifButtonModule,
    MotifTooltipModule,
    CommonModule,
    MotifDropdownModule,
    MotifModalModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './template-context-menu.component.html',
  styleUrl: './template-context-menu.component.scss',
})
export class TemplateContextMenuComponent implements ICellRendererAngularComp {
  private readonly commonService: CommonService = inject(CommonService);
  private readonly agentService: AgentService = inject(AgentService);
  private readonly router: Router = inject(Router);
  private modal = inject(MotifModal);
  private readonly loggerService: LoggerService = inject(LoggerService);
  workspaceService: WorkspaceService = inject(WorkspaceService);
  private workspaceTemplateService = inject(WorkspaceTemplateService);
  private roleUtilityService = inject(RoleUtilsService);
  private localStorageService = inject(LocalStorageService);
  private headerStateService = inject(HeaderStateService);
  private userAccessService = inject(UserAccessService);
  public getDeliverableStatus?: () => string;

  deliverableStatus: string = '';
  currentStatus: string = '';
  isDisabled: boolean = true;
  public params: any;
  public contextMenuButtonIcon = IconoirCustomMoreVerticalAlt;
  public plusIcon = IconoirPlus;
  public Placement: typeof Placement = Placement;
  public ScrollStrategy: typeof ScrollStrategy = ScrollStrategy;
  public open: boolean = false;
  public menuItems: any;
  public isHeaderCell: boolean = false;
  public workspaceTemplateItemDetails!: WorkspaceTemplateItemDetailsResponse;
  readonly confirmDialog =
    viewChild.required<ConfirmDialogComponent>('confirmDialog');
  itemToDelete: any;
  public disabledTooltip: string = '';
  showDeleteConfirm = false;
  public allowAddEdit: boolean = true;
  public currentUserRoleName: string = '';
  public currentUserGuid: any;

  /**
   * Maps a menu action string to its corresponding icon.
   * @param value The action string (e.g., 'edit', 'addMilestone').
   * @returns The icon component for the action.
   */
  public iconMappingFunction(value: string) {
    switch (value) {
      case 'edit':
        return IconoirEditPencil;
      case 'addMilestone':
        return MotifAvIcPlaylistAdd24px;
      case 'addTask':
        return IconoirPagePlusIn;
      case 'delete':
        return MotifActionIcDelete24px;
      default:
        return IconoirMoreHoriz;
    }
  }

  /**
   * Returns true if the menu item should use an SVG image instead of a Motif icon.
   */
  public isSvgIcon(value: string): boolean {
    return value === 'manage-rules';
  }

  /**
   * Returns the SVG asset path for menu items that use SVG icons.
   */
  public getSvgIconPath(value: string): string {
    if (value === 'manage-rules') {
      return 'assets/images/rule-workspace.svg';
    }
    return '';
  }

  /**
   * ag-Grid lifecycle method to initialize the cell renderer with parameters.
   * @param params The cell renderer parameters from ag-Grid.
   */
  agInit(params: ICellRendererParams<any, any, any>): void {
    this.params = params;
    if (this.params.displayName == '+') {
      params.data = { ...params.data, type: 'add' };
      this.isHeaderCell = params?.data?.type === 'add';
      this.deliverableStatus = DeliverablesConstants.StatusList.NotStarted.name;
    } else if (this.params.displayName == 'add template') {
      params.data = { ...params.data, type: 'add' };
      this.isHeaderCell = params?.data?.type === 'add';
    }
    this.canAddEdit();
    this.setMenuItems(params.data);
  }

  /**
   * ag-Grid lifecycle method to refresh the cell renderer. Returns false to indicate no refresh is needed.
   * @param params The cell renderer parameters from ag-Grid.
   * @returns Always returns false.
   */
  refresh(params: ICellRendererParams) {
    return false;
  }

  canAddEdit() {
    const userRoleData = this.localStorageService.getData(
      ApplicationConstants.SessionKeyConstants.UserAccessDetails
    );
    if (!userRoleData) {
      this.userAccessService.getUserRoleFeaturesFromApi().subscribe({
        next: user => {
          if (user) {
            this.localStorageService.saveData(
              ApplicationConstants.SessionKeyConstants.UserAccessDetails,
              JSON.stringify(user)
            );
            this.currentUserRoleName = user.userRole.roleName;
            this.currentUserGuid = user.userGuid;
          }
        },
        error() {},
        complete() {},
      });
    } else {
      let parsedData = userRoleData ? JSON.parse(userRoleData) : '';
      this.currentUserRoleName = parsedData.userRole.roleName ?? '';
      this.currentUserGuid = parsedData.userGuid ?? '';
    }

    // this.allowAddEdit = this.roleUtilityService.canAddEditDeliverable(roleName);
  }

  private setMenuItems(rowData: any): void {
    // Example: Only show 'addTask' if row type is 'Milestone'
    let items: any[];
    if (rowData?.type) {
      switch (rowData.type) {
        case CommonConstants.WorkSpaceItemTypes.Milestones:
          items =
            this.currentUserRoleName !== RoleName.EYPreparer
              ? DeliverablesConstants.MilestoneContextMenuItems
              : [];
          break;
        case CommonConstants.WorkSpaceItemTypes.Tasks:
          if (this.currentUserRoleName !== RoleName.EYPreparer) {
            items = DeliverablesConstants.TaskContextMenuItems;
          } else {
            items =
              rowData.assignedToGuidId === this.currentUserGuid
                ? DeliverablesConstants.TaskContextMenuItems.filter(
                    r => r.id == 1
                  )
                : [];
          }
          break;
        default:
          items =
            this.currentUserRoleName !== RoleName.EYPreparer
              ? DeliverablesConstants.AddMilestoneContextMenuItems
              : [];
          break;
      }
    } else {
      items = [];
    }

    let milestoneName =
      rowData?.type == CommonConstants.WorkSpaceItemTypes.Milestones
        ? rowData?.deliverables
        : '';

    // Attach workspaceItemGuid and parentGuid to each menu item
    this.menuItems = items.map(item => ({
      ...item,
      workspaceTemplateItemGuid: rowData?.guidId,
      parentGuid: rowData?.parentGuid,
      milestoneName: milestoneName,
      rowTitle: rowData?.deliverables ?? '',
      typeId: DeliverablesConstants.WorkspaceItemType.Tasks.id,
    }));
  }

  /**
   * Returns the appropriate aria-expanded value for accessibility.
   * @param open Whether the menu is open.
   * @returns 'true' if open, otherwise an empty string.
   */
  public getAriaExpanded(open: boolean): string {
    return open ? 'true' : '';
  }
  openTaskModal(component: any, item: any) {
    const modalRef = this.modal.open(AddTaskTemplatePanelComponent, {
      slideout: true,
      data: {
        action: item.action,
        workspaceTemplateItemGuid:
          item.action == 'Edit'
            ? (item.workspaceTemplateItemGuid ?? null)
            : null,
        parentGuid:
          item.action == 'Add'
            ? (item.workspaceTemplateItemGuid ?? null)
            : (item.parentGuid ?? null),
        typeId: item.typeId ?? DeliverablesConstants.WorkspaceItemType.Tasks.id,
        milestoneName: item.milestoneName ?? '',
        workspaceTemplateItemDetails: this.workspaceTemplateItemDetails,
      },
    });
    modalRef.afterClosed().subscribe(result => {
      if (result) {
        this.agentService.triggerRefresh();
      }
    });
  }
  // Handles click events on context menu items and performs actions based on the item text.
  handleDeliverableTemplateMenuItemClick(event: MouseEvent, item: any): void {
    if (this.currentUserRoleName === RoleName.ClientAdmin) {
      switch (item.text) {
        case 'edit':
          this.commonService.setIsDeliverablesFlyoutOpen(true);
          break;
        case 'Add Task':
          this.commonService.setIsDeliverablesFlyoutOpen(true);
          this.openTaskModal(AddTaskTemplatePanelComponent, item);
          break;
        case 'Edit Task':
          this.commonService.setIsDeliverablesFlyoutOpen(true);
          this.initiateTaskEdit(item);
          break;
        case 'Add Milestone':
          this.openTemplateModal(AddMilestoneTemplatePanelComponent, item);
          break;
        case 'Edit Milestone':
          this.openTemplateModal(AddMilestoneTemplatePanelComponent, item);
          break;
        case 'Delete Milestone':
          this.itemToDelete = {
            deleteMilestone: true,
            workspaceTemplateItemGuid: item?.workspaceTemplateItemGuid,
          };
          if (this.params?.context?.componentParent?.onDeleteMilestone) {
            this.params.context.componentParent.onDeleteMilestone(
              this.itemToDelete
            );
          }
          break;
        case 'Delete Task':
          this.itemToDelete = {
            deleteTask: true,
            workspaceTemplateItemGuid: item?.workspaceTemplateItemGuid,
          };
          if (this.params?.context?.componentParent?.onDeleteTask) {
            this.params.context.componentParent.onDeleteTask(this.itemToDelete);
          }
          break;
        case 'Manage Rules': {
          const selectedTemplate =
            this.workspaceTemplateService.getSelectedTemplate();
          this.router.navigate(['/rule-workspace'], {
            queryParams: {
              deliverable: item.parentGuid ?? '',
              task: item.workspaceTemplateItemGuid ?? '',
              deliverableName:
                selectedTemplate?.workSpaceTemplateName ?? '',
              taskName: item.rowTitle ?? '',
            },
          });
          break;
        }
        default:
          this.commonService.setIsDeliverablesFlyoutOpen(true);
          break;
      }
    } else {
      this.disabledTooltip =
        'You do not have the required permissions to perform this action.';
      this.loggerService.logWarning(this.disabledTooltip);
    }
  }

  openTemplateModal(component: any, item: any) {
    const modalRef = this.modal.open(component, {
      slideout: true,
      data: {
        action: item.action,
        workspaceTemplateItemGuid: item.workspaceTemplateItemGuid ?? '',
        parentGuid: item.parentGuid ?? '',
      },
    });
    modalRef.afterClosed().subscribe(result => {
      if (result) {
        this.agentService.triggerRefresh();
      }
    });
  }

  private initiateTaskEdit(item: any) {
    const tenantGuid =
      this.commonService.getState('selectedTenantGuid') ||
      JSON.parse(sessionStorage.getItem('TenantId') || '""');
    this.workspaceService
      .getWorkspaceTemplateItem(tenantGuid, item.workspaceTemplateItemGuid)
      .subscribe({
        next: res => {
          this.workspaceTemplateItemDetails = res;
          this.openTaskModal(AddTaskTemplatePanelComponent, item);
        },
      });
  }
  public onDeleteConfirmed(item: any) {
    this.showDeleteConfirm = false;
    const tenantGuid =
      this.commonService.getState('selectedTenantGuid') ||
      JSON.parse(sessionStorage.getItem('TenantId') || '""');

    this.workspaceService
      .deleteWorkspaceItem(tenantGuid, item.workspaceItemGuid)
      .subscribe({
        next: response => {},
        error: err => {
          //this.loggerService.logApiError(err?.error?.message);
        },
      });
  }
}
